import type { ReactElement, ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  BadgeIndianRupee,
  CalendarCheck,
  CalendarClock,
  CalendarX,
  CircleDot,
  CircleSlash,
  FileText,
  FileX,
  Merge,
  Package,
  PackageCheck,
  Palette,
  Pencil,
  Phone,
  Receipt,
  Redo2,
  Ruler,
  Scissors,
  Send,
  Shirt,
  StickyNote,
  ThumbsDown,
  ThumbsUp,
  Undo2,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';
import type {
  CustomerTimelineEntry,
  CustomerTimelineRef,
  CustomerTimelineRefType,
} from '@tailoring-crm/shared';
import { formatCurrency, formatDateTime } from '../../lib/format.js';
import { callHeadline } from '../activity/call-list.js';
import { TimelineRail, type TimelineRailRow } from '../timeline/timeline-rail.js';

/**
 * One customer's history: their orders, garments, money, quotations, measurements,
 * visits, alterations and designs, and the notes and calls the shop logged.
 *
 * The API decides what belongs here and in what order; this only phrases it.
 *
 * Every row that is ABOUT something says so beside its actor, as a way to it: an order's page
 * for anything that hangs off an order, and this page's own tab for the rest, when the page has
 * handed one over in `onOpen`.
 */

const KIND_ICON: Readonly<Record<string, LucideIcon>> = Object.freeze({
  customer_created: UserPlus,
  customer_updated: Pencil,
  customer_merged: Merge,
  order_booked: Package,
  order_delivered: PackageCheck,
  order_cancelled: CircleSlash,
  garment_ready: Shirt,
  garment_reworked: Redo2,
  payment_received: BadgeIndianRupee,
  payment_refunded: Undo2,
  payment_reversed: Undo2,
  invoice_issued: Receipt,
  credit_note_issued: Receipt,
  invoice_cancelled: FileX,
  quotation_created: FileText,
  quotation_sent: Send,
  quotation_accepted: ThumbsUp,
  quotation_rejected: ThumbsDown,
  measurement_taken: Ruler,
  measurement_updated: Ruler,
  appointment_booked: CalendarClock,
  appointment_completed: CalendarCheck,
  appointment_cancelled: CalendarX,
  alteration_booked: Scissors,
  alteration_delivered: PackageCheck,
  alteration_cancelled: CircleSlash,
  design_added: Palette,
  note: StickyNote,
  call: Phone,
});

export interface CustomerTimelineProps {
  entries: readonly CustomerTimelineEntry[];
  truncated?: boolean;
  /** How to open a record this page shows in one of its own tabs. Absent → the label is text. */
  onOpen?: Partial<Record<CustomerTimelineRefType, () => void>>;
}

export function CustomerTimeline({
  entries,
  truncated = false,
  onOpen = {},
}: CustomerTimelineProps): ReactElement {
  const { t } = useTranslation();

  const rows = entries.map((entry): TimelineRailRow => {
    const aside = refLink(entry.ref, onOpen, t);
    return {
      id: entry.id,
      at: entry.at,
      system: entry.source === 'system',
      icon: KIND_ICON[entry.kind] ?? CircleDot,
      headline: headline(entry, t),
      actorName: entry.actorName,
      body: entry.body,
      aside,
    };
  });

  return (
    <TimelineRail
      rows={rows}
      empty={t('customers.timeline.empty')}
      truncatedNotice={truncated ? t('customers.timeline.truncated') : null}
    />
  );
}

function refLink(
  ref: CustomerTimelineRef | null,
  onOpen: Partial<Record<CustomerTimelineRefType, () => void>>,
  t: TFunction,
): ReactNode {
  if (!ref) return null;
  const linkClass = 'font-medium text-primary underline-offset-2 hover:underline';

  const label = ref.label ?? t('customers.timeline.draft');
  if (ref.orderId) {
    return (
      <Link to={`/orders/${ref.orderId}`} className={linkClass}>
        {label}
      </Link>
    );
  }
  // A walk-in alteration has no order to open, so it opens on its own page.
  if (ref.type === 'alteration') {
    return (
      <Link to={`/alterations/${ref.id}`} className={linkClass}>
        {label}
      </Link>
    );
  }
  const open = onOpen[ref.type];
  if (open) {
    return (
      <button type="button" onClick={open} className={linkClass}>
        {label}
      </button>
    );
  }
  return <span>{label}</span>;
}

const money = (value: unknown): string =>
  typeof value === 'number' ? formatCurrency(value, { compact: true }) : '';

/** The one-line summary, phrased from the event and its `detail`. */
function headline(entry: CustomerTimelineEntry, t: TFunction): ReactNode {
  const detail = entry.detail ?? {};
  const k = (key: string, values?: Record<string, unknown>): string =>
    t(`customers.timeline.${key}`, values);

  switch (entry.kind) {
    // What staff did: a note written on the customer, a call had with them.
    case 'note':
      return t(
        typeof detail.editedAt === 'string'
          ? 'activity.timeline.noteEdited'
          : 'activity.timeline.note',
      );
    case 'call':
      return callHeadline(entry.outcome, entry.detail, t);
    case 'customer_created':
      return k('customerCreated');
    case 'customer_updated': {
      const fields = Array.isArray(detail.fields) ? (detail.fields as string[]) : [];
      if (fields.length === 0) return k('customerUpdated');
      return k('customerUpdatedFields', {
        fields: fields
          .map((field) => t(`customers.field.${field}`, { defaultValue: field }))
          .join(', '),
      });
    }
    case 'customer_merged':
      return typeof detail.duplicateName === 'string'
        ? k('customerMerged', { name: detail.duplicateName })
        : k('customerMergedUnnamed');
    case 'order_booked':
      return k('orderBooked', {
        count: typeof detail.itemCount === 'number' ? detail.itemCount : 0,
      });
    case 'order_delivered':
      return k('orderDelivered');
    case 'order_cancelled':
      return k('orderCancelled');
    case 'garment_ready':
      return k('garmentReady', { garment: String(detail.garment ?? '') });
    case 'garment_reworked':
      return k('garmentReworked', {
        garment: String(detail.garment ?? ''),
        stage: t(`stage.${String(detail.to)}`),
      });
    case 'payment_received':
    case 'payment_refunded':
      return k(entry.kind === 'payment_received' ? 'paymentReceived' : 'paymentRefunded', {
        amount: money(detail.amountPaise),
        method: t(`payments.methods.${String(detail.method)}`),
      });
    case 'payment_reversed':
      return k('paymentReversed', { amount: money(detail.amountPaise) });
    case 'invoice_issued':
      return k('invoiceIssued', { amount: money(detail.totalPaise) });
    case 'credit_note_issued':
      return k('creditNoteIssued', { amount: money(detail.totalPaise) });
    case 'invoice_cancelled':
      return k('invoiceCancelled');
    case 'quotation_created':
      return k('quotationCreated', { amount: money(detail.totalPaise) });
    case 'quotation_sent':
      return k('quotationSent');
    case 'quotation_accepted':
      return k('quotationAccepted');
    case 'quotation_rejected':
      return k('quotationRejected');
    case 'measurement_taken':
      return k('measurementTaken');
    case 'measurement_updated':
      return k('measurementUpdated');
    case 'appointment_booked':
      return k('appointmentBooked', {
        date: typeof detail.startAt === 'string' ? formatDateTime(detail.startAt) : '',
      });
    case 'appointment_completed':
      return k('appointmentCompleted');
    case 'appointment_cancelled':
      return k('appointmentCancelled');
    case 'alteration_booked':
      return k('alterationBooked', { garment: String(detail.garment ?? '') });
    case 'alteration_delivered':
      return k('alterationDelivered');
    case 'alteration_cancelled':
      return k('alterationCancelled');
    case 'design_added':
      return k('designAdded');
    default:
      return entry.kind;
  }
}
